import React, { useState, useEffect, useRef } from "react";
import OuterHeader from "./../ui/outer-header";
import AboutDashboard from "../ui/about/about-dashboard";
import AboutDentalSite from "../ui/about/about-dental-site";
import AboutPricing from "../ui/about/about-pricing";
import Footer from "./../components/footer";
import useAuth from "./../context/auth";
import { gradient_primary } from "../assets/data/colors";

const features_values = [
    { val: "30+", label: "Features" },
    { val: "100%", label: "Encrypted" },
    { val: "∞", label: "Lifetime Access" }
];

const sections = [
    { key: "dashboard", label: "Dashboard" },
    { key: "site", label: "Website" },
    { key: "fees", label: "Pricing" }
];

function useInView (threshold = 0.15){
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) setInView(true);
            },
            { threshold }
        );

        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);

    return [ref, inView];
};

function FadeIn ({ children, delay = 0, className = "" }){
    const [ref, inView] = useInView();

    return (
        <div
            ref={ref}
            className={className}
            style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(28px)",
                transition: `opacity 0.65s ease ${delay}s, transform 0.65s ease ${delay}s`
            }}
        >
            {children}
        </div>
    );
}

// the about page
const About = () => {
    const { auth } = useAuth();
    const [ activeTab, setActiveTab ] = useState("dashboard");

    return (
        <div className="min-h-screen">
            {!auth && <OuterHeader />}

            <div className="relative overflow-hidden ">
                <div className="grad-bg absolute inset-0 opacity-5 stripe-bg" />
                <div className="max-w-5xl mx-auto px-6 pt-20 pb-24 text-center relative">
                    <div style={{ opacity: 1, animation: "fadeUp 0.8s ease both" }}>
                        <span style={{background: gradient_primary}} className="inline-block text-xs font-semibold tracking-[3px] uppercase text-[#fff] bg-[#E6F1FB] dark:bg-[#0C447C]/30 dark:text-[#85B7EB] px-4 py-1.5 rounded-full mb-6">
                            Dental Management Platform
                        </span>
                        <h1 className="text-4xl md:text-7xl font-bold mb-6 leading-none">
                            <span className="grad-text dark:text-white">Everything Your</span>
                            <br />
                            <span className=" dark:text-white">Clinic Needs</span>
                        </h1>
                        <p className="text-lg text-gray-500 dark:text-gray-400 max-w-xl mx-auto leading-relaxed font-light">A lightweight, modern web dashboard built exclusively for dentists — patient records, scheduling, referrals, and more, all encrypted and accessible anywhere and anytime.</p>
                    </div>

                    <div className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-lg mx-auto">
                        {features_values.map(s => (
                            <div key={s.label} className="bg-white/80 dark:bg-[#0d1117]/80 backdrop-blur-md border border-gray-100 dark:border-gray-800 rounded-2xl py-5 sm:py-6 px-3">
                                <p className="text-xl sm:text-2xl font-semibold grad-text dark:text-white">{s.val}</p>
                                <p className="text-[11px] sm:text-xs text-gray-400 mt-0.5">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 pb-24">
                <FadeIn>
                    <div className="flex gap-2 overflow-x-auto no-scrollbar bg-white/80 dark:bg-[#0d1117]/80 backdrop-blur-md border border-gray-100 dark:border-gray-800 rounded-2xl px-3 sm:px-6 py-2 sm:py-3 mb-10 sm:mb-12 w-full sm:w-fit mx-auto">
                        {sections.map(tab => (
                            <button style={activeTab === tab.key ? {background: gradient_primary, color: "white"} : {background: "transparent"}} key={tab.key} onClick={() => setActiveTab(tab.key)} className={`whitespace-nowrap px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl text-sm sm:text-md font-medium transition-all duration-200 ${activeTab === tab.key ? "tab-active shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"}`}>
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </FadeIn>

                {activeTab === "dashboard" && (
                    <AboutDashboard />
                )}

                {activeTab === "site" && (
                    <FadeIn>
                        <AboutDentalSite />
                    </FadeIn>
                )}

                {activeTab === "fees" && (
                    <FadeIn>
                        <AboutPricing />
                    </FadeIn>
                )}
            </div>

            <Footer />
        </div>
    );
};
export default About;